import React from 'react';
import showToast from '@/components/common/Toast';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import useAssignmentsBasedGradingOptInMutation from './useAssignmentsBasedGradingOptInMutation';

interface AssignmentsBasedGradingOptInProps {
  isAssignmentsBasedGradingActive: boolean;
  setIsAssignmentsBasedGradingActive: (nextState: boolean) => void;
  stafferPath: string;
}

const OptInFeatures = () => (
  <>
    <p>Our new Grades Page gives you:</p>
    <ul className="tw-list-disc tw-ml-4 tw-flex tw-flex-col tw-gap-1.5 tw-mb-3">
      <li>A cleaner, more focused grades table—see only what you&apos;ve assigned</li>
      <li>One score per assignment to streamline grading</li>
      <li>A grading process that&apos;s more consistent with your LMS</li>
    </ul>
    <p>
      We designed these updates to save you time, simplify your workflow, and help you support every student.
    </p>
  </>
);

const EnabledDescription = () => (
  <>
    <Text className="tw-mb-5" size="sm">
      Please note, assignment scores entered through the New Grades page will not transfer
      to the Classic Grades page but will remain available upon returning to the new page.
    </Text>
    <Text className="tw-mb-5" size="sm">
      To maintain access to all entered grades, continued use of the New Grades page is suggested.
    </Text>
    <Text className="!tw-text-red" size="sm" weight="bold">
      The Classic Grades page will be retired June 30, 2027.
    </Text>
  </>
);

const AssignmentsBasedGradingOptIn = ({
  isAssignmentsBasedGradingActive,
  setIsAssignmentsBasedGradingActive,
  stafferPath,
}: AssignmentsBasedGradingOptInProps) => {
  const { mutate } = useAssignmentsBasedGradingOptInMutation({ stafferPath });

  const nextState = !isAssignmentsBasedGradingActive;

  const handleOptInOut = () => {
    mutate({ nextSetting: nextState }, {
      onSuccess: () => {
        showToast(`You have successfully opted ${nextState ? 'in' : 'out'}`, { position: 'bottom-right' });
        setIsAssignmentsBasedGradingActive(nextState);
      },
    });
  };

  return (
    <>
      <Text className="tw-mb-4" variant="heading5">
        New Grades Page: A Better Grading Experience
      </Text>
      <div className="tw-mb-4 tw-align-middle tw-flex tw-items-center">
        <Icon fill="#F69C05" icon="Warning" size={16} />
        {' '}
        <Text className="tw-pl-1" size="sm" weight="bold">
          Opting {
            isAssignmentsBasedGradingActive ? 'out will also disable' : 'in will also enable'
          } this feature for all students in your classrooms.
        </Text>
      </div>
      <div className="tw-flex tw-items-start tw-justify-between">
        <div className="tw-w-4/5">
          {isAssignmentsBasedGradingActive ? <EnabledDescription /> : <OptInFeatures />}
        </div>
        <button
          className="btn btn--purple"
          type="button"
          onClick={handleOptInOut}
        >
          {isAssignmentsBasedGradingActive ? 'Opt Out' : 'Opt In'}
        </button>
      </div>
    </>
  );
};

export default AssignmentsBasedGradingOptIn;
